import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { api, json } from '../api/client'
import type { LiveAccess, LiveOverview, StreamKey } from '../api/types'
import { billingKey, episodesKey } from './useShows'

const liveKey = (ws: string, showId: string) => ['workspace', ws, 'live', showId] as const

const PENDING = new Set(['live', 'processing', 'downloading'])

/** Worth polling: armed, on air, or a recording on its way to becoming an episode. */
function busy(live: LiveOverview | undefined): boolean {
  if (!live) return false
  return Boolean(live.input?.armed) || live.broadcasts.some((b) => PENDING.has(b.status))
}

/** A show's live input and broadcasts, polled while anything is happening. */
export function useLive(ws: string, showId: string) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: liveKey(ws, showId),
    queryFn: () => api<LiveOverview>(`/workspaces/${ws}/shows/${showId}/live`),
    refetchInterval: (q) => (busy(q.state.data) ? 5_000 : false),
  })
  // A recording that became an episode shows up in the episode list and the balance.
  const made = query.data?.broadcasts.filter((b) => b.episode_id).length ?? 0
  useEffect(() => {
    if (made === 0) return
    void queryClient.invalidateQueries({ queryKey: episodesKey(ws) })
    void queryClient.invalidateQueries({ queryKey: billingKey(ws) })
  }, [made, ws, queryClient])
  return query
}

function useLiveAction<Input>(ws: string, showId: string, action: string, body: (input: Input) => unknown) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Input) => api<LiveOverview>(`/workspaces/${ws}/shows/${showId}/live/${action}`, { method: 'POST', body: json(body(input)) }),
    onSuccess: (live) => queryClient.setQueryData(liveKey(ws, showId), live),
  })
}

/** Arm the show for a broadcast; the title names the recording's episode. */
export function useArmLive(ws: string, showId: string) {
  return useLiveAction(ws, showId, 'arm', (input: { title: string }) => input)
}

export function useDisarmLive(ws: string, showId: string) {
  return useLiveAction(ws, showId, 'disarm', () => ({}))
}

/** Check with Cloudflare Stream now. */
export function useRefreshLive(ws: string, showId: string) {
  return useLiveAction(ws, showId, 'refresh', () => ({}))
}

const streamKeyKey = (ws: string, showId: string) => ['workspace', ws, 'live', showId, 'stream-key'] as const

/** The stream key, revealed once and kept in memory (each reveal is audited). */
export function useRevealedStreamKey(ws: string, showId: string) {
  return useQuery({
    queryKey: streamKeyKey(ws, showId),
    queryFn: () => api<StreamKey>(`/workspaces/${ws}/shows/${showId}/live/key`, { method: 'POST' }),
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    retry: false,
  })
}

/** Make a new stream key: the old one stops working. Every view of the key shows the new one. */
export function useRotateStreamKey(ws: string, showId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api<StreamKey>(`/workspaces/${ws}/shows/${showId}/live/key/rotate`, { method: 'POST' }),
    onSuccess: (key) => {
      queryClient.setQueryData(streamKeyKey(ws, showId), key)
      void queryClient.invalidateQueries({ queryKey: liveKey(ws, showId), exact: true })
    },
  })
}

/** Who can watch: anyone, or only through the show's own site (signed links). */
export function useLiveAccess(ws: string, showId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (access: LiveAccess) =>
      api<LiveOverview>(`/workspaces/${ws}/shows/${showId}/live/access`, { method: 'PUT', body: json({ access }) }),
    onSuccess: (live) => queryClient.setQueryData(liveKey(ws, showId), live),
  })
}
